/**
 * W6-ADMIN-SCREENS (D401) — framework-neutral view-models for the Admin consoles whose **server routes already
 * existed and had no screen**: the food orders worklist, the operator alert bell, the rides panel on a person
 * record, the Send deliveries worklist with its custody timeline, Send claims, food post-sale cases, the operator
 * side of a store's status and hours, merchant statements and payouts, the dynamic-pricing kill switch and its
 * decision log, and the courier panel on a driver record.
 *
 * It also carries the closed reason vocabularies the wave-6 clean-up moves out of the screens (a campaign kill, a
 * company suspension and a market status change used to send one hardcoded string, so an audit row could never say
 * why).
 *
 * Rules this module keeps:
 *   - **Pure.** No React, no fetch, no clock of its own — every function takes what it needs, so it is unit-tested in
 *     Node and the screens only render it.
 *   - **The server is the authority.** Nothing here grants anything: `grantsAllow` only stops the console offering an
 *     action the server would refuse, and every refusal is still rendered from the server's answer.
 *   - **Neutral.** No country, currency, timezone or language literal. Money stays `{ amountMinor, currency }` until a
 *     screen formats it with the market's locale; instants stay UTC until `formatMarketInstant` renders them in the
 *     market's IANA zone.
 */
import {
  type CourierDispatchJob,
  type DeliveryClaimOutcome,
  type DeliveryClaimReason,
  type DeliveryClaimState,
  type DeliveryClaimView,
  type DeliveryStatus,
  type DeliveryView,
  type CustodyEventView,
  type FoodOrderStatus,
  type FoodOrderView,
  type FoodPostsaleCaseView,
  type FoodPostsaleStateView,
  type KillSwitchView,
  type MerchantPayoutView,
  type MerchantStatementView,
  type OperatingMarketView,
  type PricingAutomationDecisionView,
  type StoreOrderabilityView,
  type StoreProfileView,
  type StoreProfileWeeklyWindowView,
} from '@vello/api-client';
import { grantsAllow, type KnownGrants, type ScreenPolicy } from './console-access.js';
import { courierHoldsJob, courierJobMarketChoices, courierJobStuck, type CourierMarketChoice } from './courier-jobs.js';
import { type ScopeState } from './shell.js';
import { countryOfMarket, regionOfMarket, sameRegion } from './scope.js';

export type ScreenTone = 'neutral' | 'success' | 'warning' | 'danger';

/**
 * The markets a wave-6 worklist offers when the scope names no city — the same rule the D378 courier list uses (the
 * registered markets, narrowed to the scoped country and to the cities the grants reach). Re-exported under a neutral
 * name so a new screen does not have to import a courier helper to pick a market.
 */
export const adminScreenMarketChoices = courierJobMarketChoices;
export type { CourierMarketChoice as AdminMarketChoice };

/**
 * The market a worklist reads: the scoped city; else what the operator picked; else the only choice; else none.
 * **Never a typed market id** — the five free-text market fields this wave removes are exactly what this replaces.
 */
export function adminScreenMarket(scope: ScopeState, choices: readonly CourierMarketChoice[], picked: string | undefined): string | undefined {
  if (scope.current.city !== undefined) return scope.current.city;
  if (picked !== undefined && choices.some((c) => c.id === picked)) return picked;
  return choices.length === 1 ? choices[0]?.id : undefined;
}

/** A short, readable tail of an opaque id (a worklist is not the place for a full UUID). */
export function tailOf(id: string, length = 8): string {
  return id.length <= length ? id : `…${id.slice(-length)}`;
}

// ---- 1. food orders worklist ----------------------------------------------------------------------------

/** The statuses the operator worklist asks for: everything still in flight, plus today's terminal outcomes. */
export const FOOD_WORKLIST_STATUSES: readonly FoodOrderStatus[] = ['submitted', 'accepted', 'preparing', 'ready', 'picked_up'];

/** The statuses the "closed" filter asks for (a finished or refused order). */
export const FOOD_CLOSED_STATUSES: readonly FoodOrderStatus[] = ['delivered', 'cancelled', 'rejected'];

/** How often the worklist re-reads while the tab is visible (milliseconds) — the D372 merchant queue's cadence. */
export const FOOD_ORDERS_POLL_MS = 15_000;

/**
 * The reason codes an OPERATOR cancel may carry. The server's food cancel takes `by: 'ops'` plus a reason code, and
 * the merchant catalogue is the merchant's — an operator cancel that borrowed a merchant code would read as the
 * merchant's refusal on the audit. These are the operator's own stable codes.
 */
export const OPS_FOOD_CANCEL_REASONS: readonly string[] = [
  'ops_customer_request',
  'ops_store_unreachable',
  'ops_no_courier',
  'ops_fraud_suspicion',
  'ops_duplicate_order',
  'ops_other',
];

/** An order the operator should look at first: refused, cancelled, or waiting for the store to accept it. */
export function foodOrderNeedsAttention(order: FoodOrderView): boolean {
  return order.status === 'submitted' || order.status === 'rejected' || order.status === 'cancelled';
}

export function foodOrderTone(status: FoodOrderStatus): ScreenTone {
  if (status === 'delivered') return 'success';
  if (status === 'rejected' || status === 'cancelled') return 'danger';
  if (status === 'submitted') return 'warning';
  return 'neutral';
}

/**
 * Worklist order: the orders needing the operator first (oldest first inside that group, so the one waiting longest
 * is at the top), then the server's own order. Stable; never mutates the input.
 */
export function sortFoodOrders(orders: readonly FoodOrderView[]): readonly FoodOrderView[] {
  const at = (o: FoodOrderView): number => {
    const parsed = Date.parse(o.createdAt ?? o.updatedAt);
    return Number.isNaN(parsed) ? 0 : parsed;
  };
  return orders
    .map((order, index) => ({ order, index, rank: foodOrderNeedsAttention(order) ? 0 : 1, at: at(order) }))
    .sort((a, b) => a.rank - b.rank || (a.rank === 0 ? a.at - b.at : 0) || a.index - b.index)
    .map((e) => e.order);
}

/** Whether a food order can still be cancelled by an operator (the server refuses a terminal order with 409 anyway). */
export function foodOrderCancellable(order: FoodOrderView): boolean {
  return !['delivered', 'cancelled', 'rejected'].includes(order.status);
}

/**
 * Whether the operator's grants carry the word the operator cancel needs (`POST /v1/food/orders/:id/cancel` is
 * `order:manage`, MERCHANT-targeted → mirrored as PLATFORM). Review W6N-08: the worklist is `order:view`, which
 * SUPPORT and MERCHANT_OPS hold without `order:manage`, so the cancel was offered to them and answered 403.
 */
export function mayCancelFoodOrders(grants: KnownGrants): boolean {
  return grants === undefined || grantsAllow(grants, { permission: 'order:manage', target: 'PLATFORM' });
}

/** The market of an order as the server answers it (used to pick the timezone its times render in). */
export function foodOrderMarket(order: FoodOrderView): string | undefined {
  return order.scope?.marketId;
}

/**
 * Whether an order belongs on the page for the console scope (the server's worklist takes no market, so the console
 * narrows it and says so). A scoped city keeps that city's orders; a scoped state the orders of its markets; review W6N-13: a scoped COUNTRY used to keep every
 * country's orders, so a country-scoped operator read another country's worklist as its own.
 */
export function foodOrderInScope(order: FoodOrderView, scope: { readonly country?: string; readonly region?: string; readonly city?: string }, markets: readonly OperatingMarketView[] = []): boolean {
  const market = foodOrderMarket(order);
  if (scope.city !== undefined) return market === scope.city;
  // A state keeps only the orders of the markets the catalog places in it (an order whose market is unknown is outside).
  if (scope.region !== undefined) return market !== undefined && sameRegion(regionOfMarket(market, markets), scope.region);
  if (scope.country !== undefined) return market !== undefined && countryOfMarket(market) === scope.country.toUpperCase();
  return true;
}

// ---- 2. operator alerts (the bell) ----------------------------------------------------------------------

/** The queues the bell counts. Each one is a read the console already makes on a screen of its own. */
export type OperatorAlertKey = 'courier_stuck' | 'safety_incidents' | 'risk_cases' | 'payouts_pending' | 'partner_payments' | 'compensation_requests' | 'platform_alerts';

export const OPERATOR_ALERT_KEYS: readonly OperatorAlertKey[] = [
  'courier_stuck',
  'safety_incidents',
  'risk_cases',
  'payouts_pending',
  'partner_payments',
  'compensation_requests',
  'platform_alerts',
];

/** The route each alert opens, and the word + target its read needs (mirrors `SCREEN_POLICIES`). */
export const OPERATOR_ALERT_SOURCES: Readonly<Record<OperatorAlertKey, { readonly route: string; readonly permission: ScreenPolicy }>> = {
  courier_stuck: { route: 'courier_jobs', permission: { permission: 'courier_job:view', target: 'GEO' } },
  safety_incidents: { route: 'safety', permission: { permission: 'safety:view', target: 'PLATFORM' } },
  risk_cases: { route: 'safety', permission: { permission: 'safety:view', target: 'PLATFORM' } },
  payouts_pending: { route: 'finance_payouts', permission: { permission: 'payout:view', target: 'PLATFORM' } },
  partner_payments: { route: 'finance_partner_billing', permission: { permission: 'partner_billing:view', target: 'PLATFORM' } },
  compensation_requests: { route: 'finance', permission: { permission: 'ledger:view', target: 'PLATFORM' } },
  // D536: the platform's own alerts (the alert center: high fares, risk areas, a rider's debt settled ...) not read yet
  platform_alerts: { route: 'alert_center', permission: { permission: 'alert:view', target: 'GEO' } },
};

/**
 * One line of the bell. `count` is a **real server count**, never invented (D358 removed the fabricated badges);
 * `state` says why there is no number yet, so the popover can say "loading" or "could not read" instead of "0".
 */
export interface OperatorAlert {
  readonly key: OperatorAlertKey;
  readonly route: string;
  /** `needs_market`: a per-market queue while the scope names no city — not asked, and not a failure (W6N-11). */
  readonly state: 'loading' | 'ready' | 'unavailable' | 'needs_market';
  readonly count: number;
  readonly tone: ScreenTone;
  /** The count is a lower bound (a capped queue, or a read that saw only a full window): drawn `3+` (D560). */
  readonly atLeast?: boolean;
}

/**
 * The raw counts a read produced: a number when it answered, `undefined` while loading, `null` when it was refused,
 * `'needs_market'` when the queue is per market and the scope names none (review W6N-11: that used to be `null`, so
 * every globally scoped operator saw "could not read" and "the total is incomplete" on a bell nothing had refused).
 */
export type AlertCount = number | undefined | null | 'needs_market';

/**
 * Whether the bell may ask for a queue at all: never for one the operator's grants cannot read (it would only earn a
 * 403 every minute, and `buildOperatorAlerts` drops the line anyway). An unknown authority asks — the server decides.
 */
export function alertReadAllowed(key: OperatorAlertKey, grants: KnownGrants): boolean {
  return grants === undefined || grantsAllow(grants, OPERATOR_ALERT_SOURCES[key].permission);
}

export interface OperatorAlertCounts {
  readonly courierStuck: AlertCount;
  readonly safetyIncidents: AlertCount;
  readonly riskCases: AlertCount;
  readonly payoutsPending: AlertCount;
  readonly partnerPayments: AlertCount;
  readonly compensationRequests: AlertCount;
  /** D536: the unread alerts of the alert center (absent = not read yet). */
  readonly platformAlerts?: AlertCount;
  /** D560: the queues whose number is a lower bound. */
  readonly atLeast?: readonly OperatorAlertKey[];
}

const ALERT_TONE: Readonly<Record<OperatorAlertKey, ScreenTone>> = {
  courier_stuck: 'danger',
  safety_incidents: 'danger',
  risk_cases: 'warning',
  payouts_pending: 'warning',
  partner_payments: 'warning',
  compensation_requests: 'warning',
  platform_alerts: 'warning',
};

/**
 * The bell's lines, in the order the operator reads them: the ones with something waiting first (biggest count
 * first), then the quiet ones, then the ones that could not be read. A queue this operator's grants cannot read is
 * **left out entirely** — a count it can never open is noise, not an alert.
 */
export function buildOperatorAlerts(counts: OperatorAlertCounts, grants: KnownGrants): readonly OperatorAlert[] {
  const raw: Readonly<Record<OperatorAlertKey, AlertCount>> = {
    courier_stuck: counts.courierStuck,
    safety_incidents: counts.safetyIncidents,
    risk_cases: counts.riskCases,
    payouts_pending: counts.payoutsPending,
    partner_payments: counts.partnerPayments,
    compensation_requests: counts.compensationRequests,
    platform_alerts: counts.platformAlerts,
  };
  const lines: OperatorAlert[] = [];
  for (const key of OPERATOR_ALERT_KEYS) {
    const source = OPERATOR_ALERT_SOURCES[key];
    if (grants !== undefined && !grantsAllow(grants, source.permission)) continue;
    const value = raw[key];
    const state: OperatorAlert['state'] = value === undefined ? 'loading' : value === null ? 'unavailable' : value === 'needs_market' ? 'needs_market' : 'ready';
    lines.push({ key, route: source.route, state, count: typeof value === 'number' ? value : 0, tone: ALERT_TONE[key], ...(counts.atLeast?.includes(key) === true && state === 'ready' ? { atLeast: true } : {}) });
  }
  return lines
    .map((line, index) => ({ line, index, rank: line.state === 'unavailable' ? 3 : line.state === 'needs_market' ? 2 : line.count > 0 ? 0 : 1 }))
    .sort((a, b) => a.rank - b.rank || (a.rank === 0 ? b.line.count - a.line.count : 0) || a.index - b.index)
    .map((e) => e.line);
}

/** What the bell's badge shows: the sum of the queues that answered. Zero means "read, and nothing waiting". */
export function alertsTotal(alerts: readonly OperatorAlert[]): number {
  return alerts.reduce((n, a) => n + (a.state === 'ready' ? a.count : 0), 0);
}

/** Whether the total is a lower bound: some queue that answered reported only "at least" (D560). */
export function alertsAtLeast(alerts: readonly OperatorAlert[]): boolean {
  return alerts.some((a) => a.state === 'ready' && a.atLeast === true);
}

/** Whether any queue could not be read — the bell says so instead of implying everything is quiet. */
export function alertsDegraded(alerts: readonly OperatorAlert[]): boolean {
  return alerts.some((a) => a.state === 'unavailable');
}

// ---- 3. the rides panel on a person record --------------------------------------------------------------

/**
 * What the rides panel of a person record asks `POST /v1/ops/ride-support/rides/search` for (D413: in the body).
 *
 * **The market is always sent.** Without it the server treats a UUID as a RIDE id and answers an empty list for an
 * account id (`ride-support-routes.ts`: the id branch returns `{ rides: [] }` when no market was given), so the panel
 * would show "no rides" for a rider that has plenty. With a market the same UUID is searched as a rider/driver id.
 * `undefined` means the console has no market to search in and must say so rather than send a query that lies.
 */
export function personRidesQuery(accountId: string, market: string | undefined): { readonly q: string; readonly market: string } | undefined {
  const q = accountId.trim();
  if (q === '' || market === undefined || market.trim() === '') return undefined;
  return { q, market: market.trim() };
}

/** How many rides the person panel lists (the newest ones; the full search lives on Operations › Rides). */
export const PERSON_RIDES_LIMIT = 10;

// ---- 4. Send deliveries worklist + custody timeline ------------------------------------------------------

/** The Send statuses the worklist offers, in the order a delivery walks them. */
export const SEND_WORKLIST_STATUSES: readonly DeliveryStatus[] = [
  'confirmed',
  'courier_assigned',
  'picked_up',
  'in_transit',
  'delivered',
  'return_initiated',
  'returned',
  'failed',
  'cancelled',
  'draft',
];

export function deliveryTone(status: DeliveryStatus): ScreenTone {
  if (status === 'delivered') return 'success';
  if (status === 'failed' || status === 'cancelled' || status === 'returned') return 'danger';
  if (status === 'return_initiated' || status === 'draft') return 'warning';
  return 'neutral';
}

/** One step of the custody chain, oldest first, as the operator reads it. */
export interface CustodyStep {
  readonly eventId: string;
  readonly seq: number;
  readonly kind: CustodyEventView['kind'];
  readonly from: CustodyEventView['from'];
  readonly to: CustodyEventView['to'];
  readonly at: string;
  readonly proofRef?: string;
}

/** The custody chain in sequence order (the store answers by market; the order is not guaranteed). */
export function custodyTimeline(events: readonly CustodyEventView[]): readonly CustodyStep[] {
  return [...events]
    .sort((a, b) => a.seq - b.seq)
    .map((e) => ({ eventId: e.eventId, seq: e.seq, kind: e.kind, from: e.from, to: e.to, at: e.at, ...(e.proofRef !== undefined ? { proofRef: e.proofRef } : {}) }));
}

/** Who holds the parcel right now, from the last custody step; `undefined` when nothing has been recorded yet. */
export function currentCustodyHolder(events: readonly CustodyEventView[]): CustodyEventView['to'] | undefined {
  const steps = custodyTimeline(events);
  return steps[steps.length - 1]?.to;
}

/**
 * Whether the Send's handover PIN budget ran out (D389, ADR-0058). A locked Send verifies no further code, so the
 * operator sees it as the reason the courier is stuck instead of a silent refusal on the courier's phone.
 */
export function deliveryPinLocked(delivery: DeliveryView): boolean {
  return delivery.handoverPinLock !== undefined;
}

// ---- 5. Send claims -------------------------------------------------------------------------------------

export const CLAIM_REASONS: readonly DeliveryClaimReason[] = ['lost', 'damaged', 'not_delivered', 'wrong_item', 'delay'];
export const CLAIM_OUTCOMES: readonly DeliveryClaimOutcome[] = ['upheld', 'partial', 'rejected'];

/** The stable reason codes an operator's claim decision may carry (the audit keeps the code, not the words). */
export const CLAIM_DECISION_REASONS: readonly string[] = [
  'claim_evidence_confirms',
  'claim_evidence_insufficient',
  'claim_carrier_fault',
  'claim_sender_packaging',
  'claim_recipient_unavailable',
  'claim_other',
];

export function claimTone(state: DeliveryClaimState): ScreenTone {
  if (state === 'closed') return 'success';
  if (state === 'appealed') return 'danger';
  if (state === 'opened') return 'warning';
  return 'neutral';
}

/** The transitions the console offers for a claim state (the server decides; this only hides what it would refuse). */
export function claimActions(claim: DeliveryClaimView): readonly ('investigate' | 'decide' | 'close')[] {
  if (claim.state === 'opened') return ['investigate'];
  if (claim.state === 'investigating' || claim.state === 'appealed') return ['decide'];
  if (claim.state === 'decided') return ['close'];
  return [];
}

/** Whether the operator's grants carry the word a claim decision needs (`claim:decide`, platform-targeted). */
export function mayDecideClaims(grants: KnownGrants): boolean {
  return grants === undefined || grantsAllow(grants, { permission: 'claim:decide', target: 'PLATFORM' });
}

/**
 * Whether the operator's grants carry the word `investigate` and `close` need (`claim:manage`). Review W6N-09: both
 * were gated on `claim:decide`, a word their routes never check.
 */
export function mayManageClaims(grants: KnownGrants): boolean {
  return grants === undefined || grantsAllow(grants, { permission: 'claim:manage', target: 'PLATFORM' });
}

/**
 * A claim written before D397 took its decider and evidence submitters from request bodies. The screen labels it so an
 * operator never reads a self-declared actor as a signed-in one.
 */
export function claimActorsAreLegacy(claim: DeliveryClaimView): boolean {
  return claim.actorsFrom === 'LEGACY_BODY';
}

// ---- 6. food post-sale cases -----------------------------------------------------------------------------

export function postsaleTone(state: FoodPostsaleStateView): ScreenTone {
  if (state === 'resolved') return 'success';
  if (state === 'rejected' || state === 'appealed') return 'danger';
  if (state === 'opened') return 'warning';
  return 'neutral';
}

/** The stable reason codes an operator's post-sale decision may carry. */
export const POSTSALE_DECISION_REASONS: readonly string[] = [
  'postsale_evidence_confirms',
  'postsale_evidence_insufficient',
  'postsale_merchant_fault',
  'postsale_courier_fault',
  'postsale_customer_withdrew',
  'postsale_other',
];

/**
 * The transitions the console offers for a post-sale case state. D854 (L3-011, four eyes): the money decision (`resolve`) is never
 * offered to the operator who opened or submitted the case (`meId`); the server refuses it too (`POSTSALE_SELF_DECISION`).
 */
export function postsaleActions(view: FoodPostsaleCaseView, meId?: string, selfApproval = false): readonly ('submit' | 'resolve' | 'reject')[] {
  if (view.state === 'opened') return ['submit'];
  if (view.state === 'under_review' || view.state === 'appealed') {
    // D868: a GLOBAL super administrator resolves a case they opened or submitted.
    const self = !selfApproval && meId !== undefined && (view.openedByOperatorId === meId || view.submittedByOperatorId === meId);
    return self ? ['reject'] : ['resolve', 'reject'];
  }
  return [];
}

/** Whether the operator's grants carry the word a post-sale decision needs (`postsale:decide`). */
export function mayDecidePostsale(grants: KnownGrants): boolean {
  return grants === undefined || grantsAllow(grants, { permission: 'postsale:decide', target: 'PLATFORM' });
}

/**
 * Whether the operator's grants carry the word `submit` needs (`POST /v1/food-postsale/cases/:id/submit` is
 * `postsale:manage`). Review W6N-06 (Codex P2): submit was gated on `postsale:decide`, so SUPPORT — which holds
 * `manage` and deliberately not `decide` — could open the queue but never send a case for review.
 */
export function mayManagePostsale(grants: KnownGrants): boolean {
  return grants === undefined || grantsAllow(grants, { permission: 'postsale:manage', target: 'PLATFORM' });
}

/** A case written before D397 declared its roles in request bodies — the screen says so. */
export function postsaleActorsAreLegacy(view: FoodPostsaleCaseView): boolean {
  return view.actorsFrom === 'LEGACY_BODY';
}

// ---- 7. store status, hours and pause (the operator's side) ----------------------------------------------

export function storeAvailabilityTone(record: StoreProfileView): ScreenTone {
  const mode = record.availability.mode;
  if (mode === 'paused') return 'danger';
  if (mode === 'busy') return 'warning';
  return record.profile.status === 'active' ? 'success' : 'warning';
}

/**
 * D849 (P0-012): what a store can take NOW is the server's orderability decision (`GET /v1/store-profile/profiles/:id/orderability`: the same gate a
 * rider's order meets), not the merchant's own open/busy/paused switch - a store with no hours is "open" on that switch and still refuses every order
 * with `OUTSIDE_HOURS`. This is the reason the screen words: the gate's own code, except that `OUTSIDE_HOURS` with no weekly hours at all reads as
 * `NO_HOURS` (nothing is "outside" hours that were never set). `undefined` = the decision could not be read (the screen says so, it never guesses "open").
 */
export function storeOrderableReason(record: StoreProfileView, decision: StoreOrderabilityView | undefined): string | undefined {
  if (decision === undefined) return undefined;
  if (decision.orderable) return 'ORDERABLE';
  if (decision.reasonCode === 'OUTSIDE_HOURS' && (record.hours === undefined || record.hours.regular.length === 0)) return 'NO_HOURS';
  return decision.reasonCode;
}

/** The tone of the orderable state: green only when the server says orders are accepted; unknown stays neutral. */
export function storeOrderableTone(decision: StoreOrderabilityView | undefined): ScreenTone {
  if (decision === undefined) return 'neutral';
  return decision.orderable ? 'success' : 'warning';
}

/** The stable reason codes an operator pause may carry (a pause always carries a reason and an expiry). */
export const STORE_PAUSE_REASONS: readonly string[] = [
  'store_kitchen_overloaded',
  'store_supply_shortage',
  'store_staff_shortage',
  'store_equipment_failure',
  'store_compliance_hold',
  'store_other',
];

/** How long an operator pause may last, in minutes — the choices the screen offers instead of a typed instant. */
export const STORE_PAUSE_MINUTES: readonly number[] = [15, 30, 60, 120, 240];

/** The expiry instant a pause of `minutes` ends at, from a UTC `now`. Neutral: the caller supplies the clock. */
export function storePauseExpiry(nowIso: string, minutes: number): string | undefined {
  const now = Date.parse(nowIso);
  if (Number.isNaN(now) || !Number.isInteger(minutes) || minutes <= 0) return undefined;
  return new Date(now + minutes * 60_000).toISOString();
}

/** A weekly window split into the day it starts on and the wall-clock minutes, for rendering (minute-of-week is 0..10079). */
export interface WeeklyWindowParts {
  readonly startDay: number;
  readonly startMinuteOfDay: number;
  readonly endDay: number;
  readonly endMinuteOfDay: number;
  /** True when the window crosses the week boundary (`end <= start`) — it runs into the next week. */
  readonly wraps: boolean;
}

const MINUTES_PER_DAY = 24 * 60;
const MINUTES_PER_WEEK = 7 * MINUTES_PER_DAY;

/** Split a half-open `[start, end)` minute-of-week window into day + minute-of-day parts. */
export function weeklyWindowParts(window: StoreProfileWeeklyWindowView): WeeklyWindowParts | undefined {
  const { startMinuteOfWeek: start, endMinuteOfWeek: end } = window;
  if (!Number.isInteger(start) || !Number.isInteger(end) || start < 0 || end < 0 || start >= MINUTES_PER_WEEK || end > MINUTES_PER_WEEK) return undefined;
  // Review W6N-07 (Codex P2): the end of the week (10080) is Sunday 24:00. It used to become endDay 6 with minute
  // `10080 % 1440 = 0`, i.e. Sunday 00:00 — the seeded always-open window read as closing a whole day early.
  const endOfWeek = end === MINUTES_PER_WEEK;
  return {
    startDay: Math.floor(start / MINUTES_PER_DAY),
    startMinuteOfDay: start % MINUTES_PER_DAY,
    endDay: endOfWeek ? 6 : Math.floor(end / MINUTES_PER_DAY),
    endMinuteOfDay: endOfWeek ? MINUTES_PER_DAY : end % MINUTES_PER_DAY,
    wraps: end <= start,
  };
}

/**
 * `HH:MM` for a minute-of-day (24-hour, zero-padded) — a clock, never a locale-formatted time. Exactly one day
 * (1440) is `24:00`, the end-of-day boundary `weeklyWindowParts` uses for the end of the week.
 */
export function minuteOfDayToClock(minute: number): string {
  if (minute === MINUTES_PER_DAY) return '24:00';
  const m = ((minute % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** Whether the operator's grants carry the word a pause/resume needs (`store:operate`, mirrored as platform). */
export function mayOperateStores(grants: KnownGrants): boolean {
  return grants === undefined || grantsAllow(grants, { permission: 'store:operate', target: 'PLATFORM' });
}

// ---- 8. merchant statements and payouts ------------------------------------------------------------------

/**
 * What the ledger owes the merchant for a statement: its net payable **minus every payout already initiated or paid
 * against it**. A failed payout returns its amount, so it does not reduce what is owed.
 *
 * This is the number the screen shows instead of a typed amount: W6-EARNINGS A2 caps a payout at what the ledger
 * owes, so an operator that typed anything else would only ever produce a refusal.
 */
export function statementOwed(statement: MerchantStatementView, payouts: readonly MerchantPayoutView[] | undefined): { readonly amountMinor: bigint; readonly currency: string } | undefined {
  // Review W6N-05 (Codex P1): `undefined` is "the payouts of this statement could not be read". Netting nothing
  // would present the FULL net payable as still owed — an overstated balance — so the answer is unknown.
  if (payouts === undefined) return undefined;
  // The statement names its own currency; a net payable that disagrees with it cannot be netted at all.
  if (!/^-?\d+$/.test(statement.netPayable.amountMinor) || statement.netPayable.currency !== statement.currency) return undefined;
  let owed = BigInt(statement.netPayable.amountMinor);
  for (const payout of payouts) {
    if (payout.statementId !== statement.statementId) continue;
    if (payout.state === 'failed') continue;
    if (!/^-?\d+$/.test(payout.amount.amountMinor) || payout.amount.currency !== statement.currency) return undefined;
    owed -= BigInt(payout.amount.amountMinor);
  }
  return { amountMinor: owed, currency: statement.currency };
}

export function payoutStateTone(state: MerchantPayoutView['state']): ScreenTone {
  if (state === 'paid') return 'success';
  if (state === 'failed') return 'danger';
  return 'warning';
}

/**
 * Why the console does not offer "mark paid" on a payout, or `undefined` when it does:
 *   - `not_pending` — it is already paid or failed;
 *   - `unattributed` — it was initiated before D385 recorded a maker, so the server can never let anyone settle it;
 *   - `self_settlement` — the signed-in operator initiated it, and the server refuses `SELF_APPROVAL`;
 *   - `no_permission` — this operator's grants do not carry `payout:settle`.
 */
export type MarkPaidBlock = 'not_pending' | 'unattributed' | 'self_settlement' | 'no_permission' | undefined;

export function markPaidBlock(payout: MerchantPayoutView, operatorId: string | undefined, grants: KnownGrants): MarkPaidBlock {
  if (payout.state !== 'pending') return 'not_pending';
  if (grants !== undefined && !grantsAllow(grants, { permission: 'payout:settle', target: 'PLATFORM' })) return 'no_permission';
  if (payout.initiatedBy === null) return 'unattributed';
  if (payout.initiatedBy !== undefined && operatorId !== undefined && payout.initiatedBy === operatorId) return 'self_settlement';
  return undefined;
}

// ---- 9. dynamic pricing: kill switch + decision log -------------------------------------------------------

/** The scope a kill switch is read and set at. The pilot's scope is the market id; the platform scope is `global`. */
export const PRICING_GLOBAL_SCOPE = 'global';

/**
 * The scope the pricing console reads and writes, given the console scope and what the operator picked.
 *
 * Review W6N-03 (Codex P1): the screen used to take the scoped city ONCE, on mount, so switching the console scope
 * from market A to market B kept every read — and every kill-switch WRITE — on A while the chrome said B. Now:
 * the scoped city always wins; with no scoped city the operator's pick is kept while it is still on offer (or is the
 * platform scope); anything else falls back to the platform scope, never to a market the scope no longer shows.
 */
export function pricingScopeFor(scope: ScopeState, choices: readonly CourierMarketChoice[], picked: string): string {
  if (scope.current.city !== undefined) return scope.current.city;
  if (picked === PRICING_GLOBAL_SCOPE || choices.some((c) => c.id === picked)) return picked;
  return PRICING_GLOBAL_SCOPE;
}

/**
 * A scope whose kill switch was never written. `GET /v1/pricing-automation/kill-switch` answers 404 for it, and the
 * decision route treats a missing switch as NOT killed — so the screen shows it live at revision 0 and lets the
 * operator write the first row (review W6N-02, Codex P1: the 404 left the screen with no switch and no action).
 */
export function unsetPricingKillSwitch(scope: string): KillSwitchView {
  return { scope, killed: false, revision: 0 };
}

/** The kill switch as the screen states it: killed means automation proposes nothing, whatever the model says. */
export function pricingKillSwitchTone(view: KillSwitchView | undefined): ScreenTone {
  if (view === undefined) return 'neutral';
  return view.killed ? 'danger' : 'success';
}

export function pricingDecisionTone(decision: PricingAutomationDecisionView): ScreenTone {
  if (decision.decision === 'APPLY') return 'success';
  return decision.reason === 'KILL_SWITCH' ? 'danger' : 'warning';
}

/** The decision log newest first (the store answers in write order). */
export function sortPricingDecisions(decisions: readonly PricingAutomationDecisionView[]): readonly PricingAutomationDecisionView[] {
  return [...decisions].sort((a, b) => {
    const at = Date.parse(b.at) - Date.parse(a.at);
    return Number.isNaN(at) ? 0 : at;
  });
}

/** A basis-point multiplier as a plain multiplier string with two decimals (1.25×), never a percentage of a percentage. */
export function multiplierFromBasisPoints(bp: number): string {
  return (bp / 10_000).toFixed(2);
}

/** Whether the operator's grants carry the word the kill switch needs (`pricing:kill_switch`). */
export function mayFlipPricingKillSwitch(grants: KnownGrants): boolean {
  return grants === undefined || grantsAllow(grants, { permission: 'pricing:kill_switch', target: 'PLATFORM' });
}

// ---- 10. the courier panel on a driver record -------------------------------------------------------------

/**
 * The courier jobs of ONE driver, from the market's list. The operator surface answers a whole market
 * (`GET /v1/courier-dispatch/jobs?marketId=`), so the record narrows it to the jobs this person holds or held —
 * the stuck ones first, exactly as the Operations list orders them.
 */
export function courierJobsOfPerson(jobs: readonly CourierDispatchJob[], personId: string): readonly CourierDispatchJob[] {
  return jobs
    .filter((j) => j.courierId === personId)
    .map((job, index) => ({ job, index, rank: courierJobStuck(job) !== undefined ? 0 : courierHoldsJob(job) ? 1 : 2 }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((e) => e.job);
}

/** How many courier jobs the person panel lists (the rest live on Operations › Couriers on jobs). */
export const PERSON_COURIER_JOBS_LIMIT = 5;

// ---- wave-6 clean-up: the reason vocabularies the screens used to hardcode --------------------------------

/**
 * Why a campaign is killed. Before D401 the Admin sent one fixed string (`operator kill switch`) for every kill, so
 * the growth audit could not tell a budget stop from a compliance stop.
 */
export const CAMPAIGN_KILL_REASONS: readonly string[] = [
  'campaign_budget_exhausted',
  'campaign_underperforming',
  'campaign_compliance_hold',
  'campaign_pricing_conflict',
  'campaign_other',
];

/**
 * Why a company (a business account) is suspended. Before D401 every suspension carried `operator_suspension`, which
 * says who did it and nothing about why.
 */
export const COMPANY_SUSPEND_REASONS: readonly string[] = [
  'company_unpaid_invoice',
  'company_credit_limit_breached',
  'company_fraud_suspicion',
  'company_contract_ended',
  'company_customer_request',
  'company_other',
];

/**
 * Why a market's status changed. Before D401 the change fired straight from the select's `onChange`, with no
 * confirmation and no reason — a mis-click retired a live market and left no record of the intent.
 */
export const MARKET_STATUS_REASONS: readonly string[] = [
  'market_launch_approved',
  'market_pilot_extended',
  'market_paused_by_operations',
  'market_regulatory_hold',
  'market_retired_end_of_service',
  'market_other',
];

// ---- wave-6 clean-up: the five screens that rendered UTC as if it were local ------------------------------

/**
 * The market zone inside a `@vello/format` context, or `undefined` when the context carries no market zone.
 *
 * Five Admin screens built their format context with `timeZone: 'UTC'` and rendered instants through it, so an
 * operator read a UTC wall clock with no marker and took it for local time. They now build the context from the
 * operator's scope and render instants through `formatMarketInstant`, which needs `undefined` (not the string
 * `'UTC'`) to know it must add the explicit `UTC` marker. This is that translation, in one place.
 */
export function marketZoneOfContext(context: { readonly timeZone: string }): string | undefined {
  return context.timeZone === 'UTC' ? undefined : context.timeZone;
}
